import { useCallback } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  combineRunProps,
  parseErrorResponse,
  useRequestWithFeedback,
  useIdempotencyErrorHandling,
  type OverrideRunProps,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import utils from "../../../../utils";
import { useFetchTemplates } from "../../entities/swiftwireTemplates/requests";

import { API } from "./api";
import { useInternationalWireContext } from "./InternationalWireFormProvider";

export const useCreateInternationalWire = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateSwiftTransaction.Response,
    API.CreateSwiftTransaction.Error
  >();
  const { getValues } = useInternationalWireContext();
  const values = getValues();
  const t = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "wire-idempotency-error",
  );
  const isUSDSend = !values.quote || values.quote?.specified_currency === "USD";
  const createInternationalWire = useCallback(
    async (
      idempotencyKey: UUID,
      recipient: API.Recipient,
      user: API.User,
      deviceId?: string,
      deviceRequestId?: string,
      overrideRunProps: OverrideRunProps<
        API.CreateSwiftTransaction.Response,
        API.CreateSwiftTransaction.Error
      > = {},
    ) => {
      if (recipient.swift_destination?.id) {
        const updatedValues = {
          amount: isUSDSend
            ? utils.dollarsToPennies(values.amount)
            : utils.dollarsToPennies(
                values.quote?.converted_amount || values.amount,
              ),
          from_account: values.fromAccountId as API.AccountId,
          destination: recipient.swift_destination?.id,
          user: user.id,
          wire_reason: values.wireReason,
          memo: values.memo,
          note: values.note,
          device_id: deviceId,
          device_request_id: deviceRequestId,
          quote: values.quote,
        };
        return send({
          action: API.createInternationalWire(idempotencyKey, updatedValues),
          // TODO: add to redux store
          onData: (swiftTransaction) => {},
          onError: async (error) => {
            const { errors } = await parseErrorResponse(error);
            if (canHandleIdempotencyErrors(errors)) return;
            const defaultError = t.getString("international-wire-error-banner");
            throwToast({
              kind: "error",
              message: errors.length
                ? `${errors[0].description}`
                : defaultError,
            });
          },
          ...overrideRunProps,
        });
      }
      return Promise.resolve();
    },
    [send, values, t, throwToast, canHandleIdempotencyErrors, isUSDSend],
  );

  return useObjectMemo({
    createInternationalWire,
    loading,
  });
};

export const useFetchInternationalWireReasons = () => {
  const { send, loading } = useRequestWithFeedback<
    API.FetchInternationalWireReasons.Response,
    API.FetchInternationalWireReasons.Error
  >();

  const fetchInternationalWireReasons = useCallback(
    async (
      params: API.FetchInternationalWireReasons.Request,
      overrideRunProps: OverrideRunProps<
        API.FetchInternationalWireReasons.Response,
        API.FetchInternationalWireReasons.Error
      > = {},
    ) => {
      await send({
        action: API.fetchInternationalWireReasons(params),
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchInternationalWireReasons,
    loading,
  });
};

export const useFetchSupportedCurrencies = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.FetchSupportedCurrencies.Response,
    API.FetchSupportedCurrencies.Error
  >();

  const fetchSupportedCurrencies = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.FetchSupportedCurrencies.Response,
        API.FetchSupportedCurrencies.Error
      > = {},
    ) => {
      return send({
        action: API.fetchSupportedCurrencies(),
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchSupportedCurrencies,
    loading,
    errorMessage,
  });
};

export const useFetchExchangeRateQuote = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.FetchExchangeRateQuote.Response,
    API.FetchExchangeRateQuote.Error
  >();

  const fetchExchangeRateQuote = useCallback(
    (
      specifiedCurrency: string,
      amount: string,
      convertedCurrency?: string,
      overrideRunProps: OverrideRunProps<
        API.FetchExchangeRateQuote.Response,
        API.FetchExchangeRateQuote.Error
      > = {},
    ) => {
      return send({
        action: API.fetchExchangeRateQuote({
          specifiedCurrency,
          amount,
          convertedCurrency,
        }),
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      }).catch(() => {
        // send is explicitly detaching the promise chain, I don't know why
        // but this means cases the onError callback is called on still get
        // raised here.
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchExchangeRateQuote,
    loading,
    errorMessage,
  });
};

export const getUSDAmount = (
  quoteOrContract: API.FetchExchangeRateQuote.Response,
) => {
  if (quoteOrContract.converted_currency === "USD") {
    return quoteOrContract.converted_amount as unknown as Cents;
  }
  return quoteOrContract.amount as unknown as Cents;
};

/**
 * Submits an international wire using a swift wire template.
 *
 * Sends the template ID as the primary identifier, plus any user-provided
 * values (amount, wireReason, memo, etc.) for fields that are not defined on
 * the template. The server merges the template's stored values with these
 * supplemental values.
 */
export const useCreateInternationalWireViaTemplate = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateSwiftTransactionViaTemplate.Response,
    API.CreateSwiftTransactionViaTemplate.Error
  >();
  const { getValues } = useInternationalWireContext();
  const t = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");
  const { send: fetchTemplates } = useFetchTemplates();
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "wire-idempotency-error",
  );

  const createInternationalWireViaTemplate = useCallback(
    async (
      idempotencyKey: UUID,
      deviceId?: string,
      deviceRequestId?: string,
      overrideRunProps: OverrideRunProps<
        API.CreateSwiftTransactionViaTemplate.Response,
        API.CreateSwiftTransactionViaTemplate.Error
      > = {},
    ) => {
      const values = getValues();
      const template = values.swiftWireTemplate;

      if (!template) {
        return Promise.resolve();
      }

      // Build the payload: template ID is required, everything else is an override.
      // Only include fields the user explicitly entered that the template doesn't provide.
      const isUSDSend =
        !values.quote || values.quote?.specified_currency === "USD";

      const payload: API.CreateSwiftTransactionViaTemplate.Request = {
        swiftwire_template: template.id,
        version_id: template.version_id,
      };

      // User-entered overrides — only include fields the template doesn't provide
      if (values.amount && !template.amount) {
        payload.amount = isUSDSend
          ? utils.dollarsToPennies(values.amount)
          : utils.dollarsToPennies(
              values.quote?.converted_amount || values.amount,
            );
      }
      if (values.wireReason && !template.wire_reason) {
        payload.wire_reason = values.wireReason;
      }
      if (values.memo && !template.memo) {
        payload.memo = values.memo;
      }
      if (values.note && !template.note) {
        payload.note = values.note;
      }

      // Always include the quote if present (needed for FX)
      if (values.quote) {
        payload.quote = values.quote;
      }

      // Device info for MFA
      if (deviceId) {
        payload.device_id = deviceId;
      }
      if (deviceRequestId) {
        payload.device_request_id = deviceRequestId;
      }

      return send({
        action: API.createInternationalWireViaTemplate(idempotencyKey, payload),
        ...combineRunProps<
          API.CreateSwiftTransactionViaTemplate.Response,
          API.CreateSwiftTransactionViaTemplate.Error
        >(
          {
            onError: async (error) => {
              const { errors } = await parseErrorResponse(error);
              if (!canHandleIdempotencyErrors(errors)) {
                const defaultError = t.getString(
                  "international-wire-error-banner",
                );
                throwToast({
                  kind: "error",
                  message: errors.length
                    ? `${errors[0].description}`
                    : defaultError,
                });
              }
              fetchTemplates();
            },
          },
          overrideRunProps,
        ),
      });
    },
    [
      send,
      getValues,
      t,
      throwToast,
      canHandleIdempotencyErrors,
      fetchTemplates,
    ],
  );

  return useObjectMemo({
    createInternationalWireViaTemplate,
    loading,
  });
};
